import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../services/auth';
import { CheckCircle, Zap, Crown, ArrowUp, Mail } from 'lucide-react';
import apiClient from '../services/api';

/**
 * Login Page with Multiple Auth Options
 * 
 * Supports:
 * 1. Google OAuth via Emergent Auth (recommended)
 * 2. Email Magic Link via Stytch (legacy)
 * 
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [checkingUser, setCheckingUser] = useState(false);
  const [existingUser, setExistingUser] = useState(null);
  const [showEmailForm, setShowEmailForm] = useState(false);
  
  // Get URL params
  const subscribed = searchParams.get('subscribed') === 'true';
  const plan = searchParams.get('plan') || 'free';
  const billing = searchParams.get('billing') || 'monthly';

  // Check if user with this email already exists
  const checkExistingUser = async (emailToCheck) => {
    if (!emailToCheck || emailToCheck.length < 5) return;
    
    setCheckingUser(true);
    try {
      const response = await apiClient.post('/api/auth/check-user', { email: emailToCheck });
      if (response.data.exists) {
        setExistingUser(response.data.user);
      } else {
        setExistingUser(null);
      }
    } catch (err) {
      // User doesn't exist or error - that's fine
      setExistingUser(null);
    } finally {
      setCheckingUser(false);
    }
  };

  // Debounce email check
  useEffect(() => {
    const timer = setTimeout(() => {
      if (email && email.includes('@')) {
        checkExistingUser(email);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [email]);

  // Store email in session for continuity
  useEffect(() => {
    const storedEmail = sessionStorage.getItem('pending_signup_email');
    if (storedEmail) {
      setEmail(storedEmail);
    }
  }, []);

  // Handle Google OAuth login
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    // Store email for session continuity
    sessionStorage.setItem('pending_signup_email', email);
    sessionStorage.setItem('pending_plan', plan);
    sessionStorage.setItem('pending_billing', billing);

    try {
      await authService.sendMagicLink(email);
      setIsSubmitted(true);
    } catch (err) {
      setError('Failed to send magic link. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getPlanBadge = () => {
    switch (plan) {
      case 'pro':
        return (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-600/20 border border-blue-500/30 text-blue-400 text-sm mb-4">
            <Zap className="w-4 h-4" />
            <span>Pro Plan</span>
          </div>
        );
      case 'intelligence-suite':
        return (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-purple-600/20 border border-purple-500/30 text-purple-400 text-sm mb-4">
            <Crown className="w-4 h-4" />
            <span>Intelligence Suite</span>
          </div>
        );
      default:
        return null;
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
        <div className="max-w-md w-full mx-4">
          <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.55)]">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#3EF1C7]/10 mb-4">
                <svg className="w-8 h-8 text-[#3EF1C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              </div>
              <h2 className="text-2xl font-semibold text-white mb-2">Check your email</h2>
              <p className="text-[#9CB0C7] mb-4">We sent a magic link to<br/><strong className="text-white">{email}</strong></p>
              
              {/* Info about new window behavior */}
              <div className="mb-6 p-3 rounded-lg bg-[#22304A]/50 border border-[#22304A]">
                <p className="text-sm text-[#9CB0C7]">
                  <svg className="w-4 h-4 inline mr-1 text-[#9CB0C7]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  The login link will open in a <strong className="text-white">new browser tab</strong>. You can close this window after clicking the link.
                </p>
              </div>
              
              {subscribed && plan && (
                <div className="mb-6 p-3 rounded-lg bg-[#3EF1C7]/10 border border-[#3EF1C7]/30">
                  <p className="text-sm text-[#3EF1C7]">
                    <CheckCircle className="w-4 h-4 inline mr-1" />
                    Your {plan === 'intelligence-suite' ? 'Intelligence Suite' : plan === 'pro' ? 'Pro' : 'Free'} subscription is ready!
                  </p>
                </div>
              )}
              
              <button
                onClick={() => setIsSubmitted(false)}
                className="text-[#3EF1C7] hover:text-[#3EF1C7]/80 text-sm"
              >
                Use a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center" style={{background: '#0B1120'}}>
      <div className="max-w-md w-full mx-4">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Commerce Index</h1>
          <p className="text-[#9CB0C7]">Merchant Dashboard</p>
          {getPlanBadge()}
        </div>
        
        <div className="rounded-lg border border-[#22304A] bg-[#0E1426]/80 backdrop-blur p-8 shadow-[0_0_0_1px_rgba(62,241,199,0.06),0_8px_30px_rgba(3,7,18,0.55)]">
          {subscribed && (
            <div className="mb-6 p-3 rounded-lg bg-[#3EF1C7]/10 border border-[#3EF1C7]/30">
              <p className="text-sm text-[#3EF1C7] flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Payment successful! Sign in to activate your {plan === 'intelligence-suite' ? 'Intelligence Suite' : plan === 'pro' ? 'Pro' : ''} plan.
              </p>
            </div>
          )}
          
          <h2 className="text-xl font-semibold text-white mb-6 text-center">
            Sign in to continue
          </h2>

          {/* Google OAuth Button - Primary */}
          <button
            onClick={handleGoogleLogin}
            className="w-full py-3 px-4 rounded-lg font-medium text-white border-2 border-[#22304A] bg-[#111A2E] hover:bg-[#1A2437] transition-colors flex items-center justify-center gap-3 mb-4"
            data-testid="google-login-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#22304A]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#0E1426] text-[#9CB0C7]">Or</span>
            </div>
          </div>

          {!showEmailForm ? (
            /* Show magic link option */
            <button
              onClick={() => setShowEmailForm(true)}
              className="w-full py-2.5 px-4 rounded-lg font-medium text-white bg-[#111A2E] border border-[#22304A] hover:bg-[#111A2E] transition-colors flex items-center justify-center gap-2"
              data-testid="show-email-login-button"
            >
              <Mail className="w-4 h-4" />
              Continue with email
            </button>
          ) : (
            /* Email Magic Link Form */
            <form onSubmit={handleSubmit}>
              {/* Existing User Alert */}
              {existingUser && !subscribed && (
                <div className="mb-4 p-4 rounded-lg bg-[#7A5CFF]/10 border border-[#7A5CFF]/30">
                  <p className="text-sm text-white mb-2">
                    <strong>{existingUser.email}</strong> already has an account
                  </p>
                  {/* F-D14 fix: /api/auth/check-user no longer returns plan/super-admin to unauthenticated callers (it disclosed both for any email).
                      Plan + upgrade prompt are shown ONLY when the response actually carries those fields (never default an unknown plan to
                      "Free" or urge an upgrade). Restore to unconditional rendering only after an authenticated, verified-user context supplies them. */}
                  {(existingUser.is_superadmin !== undefined || existingUser.subscription_tier !== undefined) && (<>
                  <p className="text-xs text-[#9CB0C7] mb-3">
                    Current plan: <span className="text-[#3EF1C7] font-medium">
                      {existingUser.is_superadmin ? 'Super Admin' : 
                       existingUser.subscription_tier === 'intelligence-suite' ? 'Intelligence Suite' :
                       existingUser.subscription_tier === 'pro' ? 'Pro' :
                       existingUser.subscription_tier?.charAt(0).toUpperCase() + existingUser.subscription_tier?.slice(1) || 'Free'}
                    </span>
                  </p>
                  {existingUser.subscription_tier !== undefined && !existingUser.is_superadmin && existingUser.subscription_tier !== 'intelligence-suite' && (
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/');
                        setTimeout(() => {
                          const pricingSection = document.getElementById('pricing');
                          if (pricingSection) {
                            pricingSection.scrollIntoView({ behavior: 'smooth' });
                          }
                        }, 100);
                      }}
                      className="text-sm text-[#7A5CFF] hover:text-[#7A5CFF]/80 flex items-center gap-1"
                    >
                      <ArrowUp className="w-4 h-4" />
                      Upgrade your plan
                    </button>
                  )}
                  </>)}
                </div>
              )}
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#9CB0C7] mb-2">
                  Email address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="merchant@example.com"
                  required
                  className="w-full px-4 py-2.5 rounded-lg bg-[#111A2E] border border-[#22304A] text-white placeholder-[#425373] focus:outline-none focus:ring-2 focus:ring-[#3EF1C7]/60 focus:border-transparent"
                  data-testid="email-input"
                />
                {checkingUser && (
                  <p className="text-xs text-[#9CB0C7] mt-1">Checking...</p>
                )}
              </div>

              {error && (
                <div className="mb-4 p-3 rounded-lg bg-[#F55454]/10 border border-[#F55454]/20 text-[#F55454] text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-2.5 px-4 rounded-lg font-medium text-white transition-colors duration-200 mb-3"
                style={{
                  background: '#7A5CFF',
                  boxShadow: '0 0 0 1px rgba(62,241,199,.15), 0 8px 24px rgba(3,7,18,.35)'
                }}
                data-testid="send-magic-link-button"
              >
                {isSubmitting ? 'Sending...' : 'Send magic link'}
              </button>
              
              <button
                type="button"
                onClick={() => setShowEmailForm(false)}
                className="w-full text-sm text-[#9CB0C7] hover:text-white transition-colors"
              >
                ← Back to other options
              </button>
            </form>
          )}

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#22304A]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-[#0E1426] text-[#9CB0C7]">Shopify Merchant?</span>
            </div>
          </div>

          {/* Shopify App Store Button */}
          <a
            href="https://apps.shopify.com/commerce-index"
            target="_blank"
            rel="noopener noreferrer"
            className="w-full py-2.5 px-4 rounded-lg font-medium text-white border-2 border-[#96bf48] bg-[#96bf48]/10 hover:bg-[#96bf48]/20 transition-colors flex items-center justify-center gap-2"
            data-testid="shopify-install-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 109.5 124.5" fill="#96bf48">
              <path d="M74.7,14.8c0,0-1.4,0.4-3.7,1.1c-0.4-1.3-1-2.8-1.8-4.4c-2.6-5-6.5-7.7-11.1-7.7c0,0,0,0,0,0
                c-0.3,0-0.6,0-1,0.1c-0.1-0.2-0.3-0.3-0.4-0.5c-2-2.2-4.6-3.2-7.7-3.2c-6,0.2-12,4.5-16.8,12.2c-3.4,5.4-6,12.2-6.7,17.5
                c-6.9,2.1-11.7,3.6-11.8,3.7c-3.5,1.1-3.6,1.2-4,4.5c-0.3,2.5-9.5,73.5-9.5,73.5l75.6,13.1V14.6C75.5,14.7,75.1,14.7,74.7,14.8z"/>
              <path fill="#5E8E3E" d="M58.3,19.7c-0.2,0.1-0.4,0.1-0.6,0.2c-0.4-1.3-1-2.8-1.8-4.4c-2.6-5-6.5-7.7-11.1-7.7c0,0,0,0,0,0
                c-0.3,0-0.6,0-1,0.1c-0.1-0.2-0.3-0.3-0.4-0.5c-2-2.2-4.6-3.2-7.7-3.2c-6,0.2-12,4.5-16.8,12.2c-3.4,5.4-6,12.2-6.7,17.5
                c-6.9,2.1-11.7,3.6-11.8,3.7c-3.5,1.1-3.6,1.2-4,4.5c-0.3,2.5-9.5,73.5-9.5,73.5l56.6,9.8V14.6C54.5,15.6,55.9,17.1,58.3,19.7z"/>
              <path fill="#fff" d="M44.8,7.8c0,0,0,0,0,0c-0.3,0-0.6,0-1,0.1c-0.1-0.2-0.3-0.3-0.4-0.5c-2-2.2-4.6-3.2-7.7-3.2
                c-6,0.2-12,4.5-16.8,12.2c-3.4,5.4-6,12.2-6.7,17.5c-6.9,2.1-11.7,3.6-11.8,3.7c-3.5,1.1-3.6,1.2-4,4.5c-0.3,2.5-9.5,73.5-9.5,73.5
                l56.6,9.8l0-117.5C46.5,7.9,45.6,7.8,44.8,7.8z"/>
            </svg>
            Install from Shopify App Store
          </a>
        </div>
        
        {!subscribed && (
          <p className="text-center text-[#9CB0C7] text-sm mt-6">
            Don't have an account?{' '}
            <button 
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  const pricingSection = document.getElementById('pricing');
                  if (pricingSection) {
                    pricingSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }, 100);
              }}
              className="text-[#3EF1C7] hover:underline"
            >
              View pricing
            </button>
          </p>
        )}
      </div>
    </div>
  );
}
